import React, { ReactNode } from "react";
import ptBrLocale from "date-fns/locale/pt-BR";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Controller, Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { CustomLabel } from "../input/CustomLabel";
import { InputErrorMessage } from "../input/InputErrorMessage";
import { DirecoesCalendario } from "types/inputs/DatePicker/DirecoesCalendario";

type CustomDatePickerWithoutValidationProps = {
  usarValidacaoParaFormulario: false;
};

type CustomDatePickerWithValidationProps = {
  usarValidacaoParaFormulario: true;
  /**Nome do atributo que será usado no formulario para realizar as validacoes*/
  name: string;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
};

type CustomDatePickerProps = {
  label?: string;
  value?: Date;
  disabled?: boolean;
  disabledFuture?: boolean;
  disabledPast?: boolean;
  onChange?: (dataSelecionada: Date | null | undefined) => void;
  defaultValue?: Date | null;
  dispararOnChangeApenasQuandoDataIsValida?: boolean;
  popperDirection?: DirecoesCalendario;
  style?: object;
} & (CustomDatePickerWithoutValidationProps | CustomDatePickerWithValidationProps);

export const CustomDatePicker: React.FC<CustomDatePickerProps> = (props) => {
  const MIN_DATE = new Date(1901, 0, 1);

  const onChange = (dataSelecionada: Date | null | undefined) => {
    if (props.onChange) {
      if (dataSelecionada && props.dispararOnChangeApenasQuandoDataIsValida) {
        const isDataValida = !isNaN(dataSelecionada.getDate()) && dataSelecionada >= MIN_DATE;
        if (isDataValida) {
          props.onChange(dataSelecionada);
        }
      } else {
        props.onChange(dataSelecionada);
      }
    }
  };

  const renderDatePickerComValidacao = (): ReactNode => {
    if (props.usarValidacaoParaFormulario) {
      return (
        <Controller
          name={props.name}
          control={props.control}
          defaultValue={props.defaultValue}
          render={({ field, fieldState }) => (
            <>
              {props.label && <CustomLabel possuiErro={fieldState.error != null}>{props.label}</CustomLabel>}
              <DatePicker
                {...props.register(props.name)}
                {...field}
                ref={field.ref}
                openTo="day"
                label={props.label}
                formatDensity="spacious"
                format="dd/MM/yyyy"
                disabled={props.disabled}
                minDate={MIN_DATE}
                defaultValue={props.defaultValue || null}
                disableFuture={props.disabledFuture}
                disablePast={props.disabledPast}
                value={props.value}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    color: "info",
                    size: "small",
                    label: "",
                    error: fieldState.error != null,
                    sx: props.style,
                  },
                  popper: {
                    placement: props.popperDirection || "bottom-start",
                    style: { zIndex: 99999 },
                  },
                  actionBar: {
                    actions: ["clear", "today"],
                  },
                }}
                onChange={(dataSelecionada: Date | null | undefined) => {
                  field.onChange(dataSelecionada);
                  props.setValue(props.name, dataSelecionada);
                  onChange(dataSelecionada);
                }}
              />
              {fieldState.error && <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>}
            </>
          )}
        />
      );
    }
  };

  const renderDatePickerSemValidacao = (): ReactNode => {
    if (!props.usarValidacaoParaFormulario) {
      return (
        <>
          {props.label && <CustomLabel possuiErro={false}>{props.label}</CustomLabel>}
          <DatePicker
            openTo="day"
            label={props.label}
            formatDensity="spacious"
            defaultValue={props.defaultValue}
            format="dd/MM/yyyy"
            disabled={props.disabled}
            disableFuture={props.disabledFuture}
            disablePast={props.disabledPast}
            minDate={MIN_DATE}
            slotProps={{
              textField: {
                variant: "outlined",
                color: "info",
                size: "small",
                label: "",
              },
              popper: {
                placement: props.popperDirection || "bottom-start",
                style: { zIndex: 99999 },
              },
              actionBar: {
                actions: ["clear", "today"],
              },
            }}
            onChange={(dataSelecionada: Date | null) => {
              onChange(dataSelecionada);
            }}
          />
        </>
      );
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={ptBrLocale}
      localeText={{
        clearButtonLabel: "Limpar",
        todayButtonLabel: "Hoje",
      }}
    >
      {props.usarValidacaoParaFormulario ? renderDatePickerComValidacao() : renderDatePickerSemValidacao()}
    </LocalizationProvider>
  );
};
